import type { CrudAdapter } from '@botto/core';
import { streamsAdapter } from '../streams';
import type {
  SmppClient,
  SmppClientCreate,
  SmppClientUpdate,
  Stream,
  StreamCreate,
  StreamUpdate,
} from '../../types';

function toUiPort(port: string | null | undefined): number | null {
  if (port == null || port === '') return null;
  const n = Number(port);
  return Number.isFinite(n) ? n : null;
}

function toApiPort(port: number | string | null | undefined, fallback?: string): string {
  if (port == null || port === '') return fallback ?? '2775';
  return String(port);
}

function toUiInterval(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toApiInterval(value: number | string | null | undefined, fallback = 30): string {
  if (value == null || value === '') return String(fallback);
  return String(value);
}

function deriveSystemId(payload: SmppClientCreate): string {
  const fromUsername = payload.smsc_username?.trim();
  if (fromUsername) return fromUsername.slice(0, 3);
  const fromName = payload.name?.trim();
  if (fromName) return fromName.slice(0, 3);
  return 'cli';
}

function streamToSmppClient(stream: Stream): SmppClient {
  return {
    ...stream,
    port: toUiPort(stream.port),
    enquire_link_interval: toUiInterval(stream.enquire_link_interval),
    decoding_mode: stream.decoding_mode ?? null,
  };
}

function toStreamCreate(payload: SmppClientCreate): StreamCreate {
  const password = payload.password ?? '';
  const smscPassword = payload.smsc_password ?? password;
  if (!smscPassword) throw new Error('Укажите Password SMPP');
  return {
    system_id: deriveSystemId(payload),
    name: payload.name ?? null,
    description: payload.description ?? '',
    manager_id: payload.manager_id ?? null,
    partner_id: payload.partner_id ?? null,
    email: payload.email ?? '',
    password,
    host: payload.host ?? '',
    port: toApiPort(payload.port),
    smsc_username: payload.smsc_username ?? '',
    smsc_password: smscPassword,
    smpp_host: payload.smpp_host ?? '127.0.0.1',
    transport_type: payload.transport_type ?? 'TCP',
    enabled: payload.enabled ?? 'yes',
    system_type: payload.system_type ?? 'VMA',
    enquire_link_interval: toApiInterval(payload.enquire_link_interval),
    cps_max: payload.cps_max ?? 5,
    archived: payload.archived ?? false,
    decoding_mode: payload.decoding_mode ?? null,
  } as StreamCreate;
}

function toStreamUpdate(payload: SmppClientUpdate): StreamUpdate {
  const body: StreamUpdate = {
    id: payload.id as number,
    system_id: payload.system_id,
    name: payload.name,
    description: payload.description,
    manager_id: payload.manager_id,
    partner_id: payload.partner_id,
    host: payload.host,
    enabled: payload.enabled,
    smsc_username: payload.smsc_username,
    smsc_password: payload.smsc_password,
    smpp_host: payload.smpp_host,
    transport_type: payload.transport_type,
    system_type: payload.system_type,
    cps_max: payload.cps_max,
    decoding_mode: payload.decoding_mode ?? null,
  };

  if (payload.port != null) {
    body.port = toApiPort(payload.port);
  }
  if (payload.enquire_link_interval != null) {
    body.enquire_link_interval = toApiInterval(payload.enquire_link_interval, 0);
  }

  return body;
}

export const smppClientsAdapter: CrudAdapter<SmppClient, SmppClientCreate, SmppClientUpdate> = {
  async list({ offset, limit, sortName, sortDirection }) {
    const result = await streamsAdapter.list({ offset, limit, sortName, sortDirection });
    return {
      items: result.items.map(streamToSmppClient),
      total: result.total,
    };
  },
  async create(payload) {
    if (!streamsAdapter.create) throw new Error('create not supported by adapter');
    return streamsAdapter.create(toStreamCreate(payload));
  },
  async update(payload) {
    if (!streamsAdapter.update) throw new Error('update not supported by adapter');
    const data = await streamsAdapter.update(toStreamUpdate(payload));
    return streamToSmppClient(data as Stream);
  },
  async byKey(id) {
    const data = await streamsAdapter.byKey?.(id);
    if (!data) throw new Error(`Stream ${id} not found`);
    return streamToSmppClient(data);
  },
  async remove(id) {
    if (!streamsAdapter.remove) throw new Error('remove not supported by adapter');
    await streamsAdapter.remove(id);
  },
};
